import {
  GfgProfile,
  GfgProfileApiResponse,
  HackerrankProfile,
  HackerrankProfileApiResponse,
  leetcodeProfile,
  leetcodeProfileApiResponse,
  ReferralDetails,
  ReferralResponse,
  UploadType,
  UserExternalProfileLinks,
  UserExternalProfileLinksApiResponse
} from 'features/User'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useLoginState } from 'state'

import { API_ENDPOINTS } from 'config/api'
import axios from 'config/axios'

import { PaymentResponse, VerifyPaymentResponse } from './user.types'
// import { dashboard_details } from './mock'

export const fetchFileUpload = async (file: Blob, type: UploadType) => {
  const formData = new FormData()
  formData.append('file_upload', file)
  formData.append('file_upload_type', type)
  const response = await axios.put(API_ENDPOINTS.UPLOAD_FILE, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  })
  return response.data
}

export const useProfilePictureUpload = () => {
  const { user } = useLoginState()
  const queryClient = useQueryClient()

  return useMutation((file: Blob) => fetchFileUpload(file, 'profile-image'), {
    onSuccess: () => {
      queryClient.invalidateQueries('auth/user')
      if (user) {
        queryClient.invalidateQueries(['user', { username: user.username }])
      }
    }
  })
}

export const useProfileResumeUpload = () => {
  const { user } = useLoginState()
  const queryClient = useQueryClient()

  return useMutation((file: Blob) => fetchFileUpload(file, 'resume'), {
    onSuccess: () => {
      queryClient.invalidateQueries('auth/user')
      if (user) {
        queryClient.invalidateQueries(['user', { username: user.username }])
      }
    }
  })
}

export const fetchEnrolledForCourseImage = async (file: Blob) => {
  const formData = new FormData()
  formData.append('image-file', file)
  const response = await axios.post(
    API_ENDPOINTS.Upload_I_Have_Enrolled_For_Course_Image,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' }
    }
  )
  return response.data
}

export const useEnrolledForCourseImageUpload = () => {
  const { user } = useLoginState()
  const queryClient = useQueryClient()

  return useMutation((file: Blob) => fetchEnrolledForCourseImage(file), {
    onSuccess: () => {
      queryClient.invalidateQueries('auth/user')
      if (user) {
        queryClient.invalidateQueries(['user', { username: user.username }])
      }
    }
  })
}

/*
 * to get referral data
 * */

export const fetchReferralData = async () => {
  const response = await axios.get(API_ENDPOINTS.USER_REFERRAL)
  const { data } = (await response.data) as ReferralResponse
  const newReferralData = data.attributes.referrals.map((ref) => {
    const conins_earn =
      (ref.is_discord_connected && ref.registered_for_the_course ? 10 : 0) +
      (ref.modules_compeleted ? 100 : 0)
    const date = new Date(ref.created_at).toString()
    return {
      ...ref,
      conins_earn,
      created_at: date
    }
  })
  return {
    id: data.id,
    referrals: newReferralData
  }
}

export const useFetchReferralData = () => {
  return useQuery<ReferralDetails, null>(['user_referral'], () =>
    fetchReferralData()
  )
}

/**
 * Disconnect Users Discord
 */

export const fetchDisconnectDescord = async (id: number) => {
  const response = await axios.post(API_ENDPOINTS.DISCONNECT_DISCORD, {
    data: {
      attributes: {
        id
      }
    }
  })

  return response.data
}

export const useDisconnectDiscordMutation = () => {
  const { user } = useLoginState()
  const queryClient = useQueryClient()
  return useMutation(fetchDisconnectDescord, {
    onSuccess: () => {
      queryClient.invalidateQueries('auth/user')
      if (user) {
        queryClient.invalidateQueries(['user', { username: user.username }])
      }
    }
  })
}

/**
 * Update User Integration
 */
// {
//   leetcode_user_name?: string
//   gfg_user_name?: string
//   hackerrank_user_name?: string
// }
export const fetchPostUserIntegrations = async (
  data: Record<string, string>
) => {
  if (Object.keys(data).length > 0) {
    const response = await axios.put(
      `${API_ENDPOINTS.USER_INTEGRATION}/update_links`,
      {
        data: {
          attributes: data,
          type: 'user_integrations'
        }
      }
    )

    return response.data
  }
}

export const useFetchUserIntegrationsMutation = () => {
  const queryClient = useQueryClient()
  return useMutation(fetchPostUserIntegrations, {
    onSuccess: () => {
      queryClient.invalidateQueries(['user-integrations-profiles'])
    }
  })
}

/*
 * to get all external link
 * */

export const fetchGetUserIntegration = async () => {
  const response = await axios.get(`${API_ENDPOINTS.USER_INTEGRATION}/links`)

  const { data } = (await response.data) as UserExternalProfileLinksApiResponse

  return data.attributes
}

export const useFetchUserIntegrations = () => {
  return useQuery<UserExternalProfileLinks, null>(
    ['user-integrations-profiles'],
    () => fetchGetUserIntegration()
  )
}

/*
 * to get leetcode external data
 * */

export const fetchGetLeetcodeIntegration = async (leetcodeUserName: string) => {
  const response = await axios.get(
    `${API_ENDPOINTS.USER_INTEGRATION}/leetcode?username=${leetcodeUserName}`
  )

  const { data } = (await response.data) as leetcodeProfileApiResponse

  return data.attributes
}

export const useFetchLeetcodeIntegrations = (leetcodeUserName: string) => {
  return useQuery<leetcodeProfile, null>(
    ['leetcode-profile', { leetcodeUserName }],
    () => fetchGetLeetcodeIntegration(leetcodeUserName),
    {
      enabled: !!leetcodeUserName
    }
  )
}

/*
 * to get GFG external data
 * */

export const fetchGetGfgIntegration = async (gfgUserName: string) => {
  const response = await axios.get(
    `${API_ENDPOINTS.USER_INTEGRATION}/gfg?username=${gfgUserName}`
  )

  const { data } = (await response.data) as GfgProfileApiResponse

  return data.attributes
}

export const useFetchGfgIntegrations = (gfgUserName: string) => {
  return useQuery<GfgProfile, null>(
    ['gfg-profile', { gfgUserName }],
    () => fetchGetGfgIntegration(gfgUserName),
    {
      enabled: !!gfgUserName
    }
  )
}

/*
 * to get Hackerrank external data
 * */

export const fetchGetHackerrankIntegration = async (
  hackerrankUserName: string
) => {
  const response = await axios.get(
    `${API_ENDPOINTS.USER_INTEGRATION}/hackerrank?username=${hackerrankUserName}`
  )

  const { data } = (await response.data) as HackerrankProfileApiResponse
  return data.attributes
}

export const useFetchHackerrankIntegrations = (hackerrankUserName: string) => {
  return useQuery<HackerrankProfile, null>(
    ['hackerrank-profile', { hackerrankUserName }],
    () => fetchGetHackerrankIntegration(hackerrankUserName),
    {
      enabled: !!hackerrankUserName
    }
  )
}

/**
 * GitHub auth
 */

/**
 * GitHub connection
 * @param code
 */

export const fetchGithubConnect = async (code: string) => {
  const response = await axios.post(
    API_ENDPOINTS.CONNECT_GITHUB + `?code=${code}`
  )
  return response.data
}

export const useGithubConnectMutation = () => {
  return useMutation(fetchGithubConnect)
}

/**
 * For Payment
 */
type PaymentOptions = {
  amount: number
  currency: string
  description?: string
  product_price_id?: number
  redirect_url?: string
}

const fetchPatymentLink = async (paymentOptions: PaymentOptions) => {
  const response = await axios.post(API_ENDPOINTS.PAYMENT_ORDER, {
    ...paymentOptions
  })

  const { data } = response.data as PaymentResponse

  return data.attributes.payment_link
}

export const useGetPaymentLink = () => {
  return useMutation(fetchPatymentLink)
}

/**
 * Verfiy Razorpay payment
 */

export type VerifyPaymentBody = {
  razorpay_payment_id: string
  razorpay_payment_link_id: string
  razorpay_payment_link_reference_id: string
  razorpay_payment_link_status: string
  razorpay_signature: string
}

const fetchPaymentStatus = async (verifyPaymentBody: VerifyPaymentBody) => {
  const response = await axios.post(
    API_ENDPOINTS.PAYMENTS_VERIFY,
    verifyPaymentBody
  )

  const { data } = (await response.data) as VerifyPaymentResponse

  return data.attributes.status
}

export const useVerifyPayment = () => {
  return useMutation(fetchPaymentStatus)
}
